// Libraries
import { Card, Flex, Text } from '@chakra-ui/react';
// Components
import { RatingInfo } from '@molecules/index';
// Utils
import { parseToThousands } from '@helpers/index';
import text from '@translations/en.json';
// Types
import { Rating } from '@utils/index';
import { CiiRatingCardProps } from './CiiRatingCard.types';

/**
 * A card component to display Cii Ratings both for the year to date and forecasted values.
 *
 * @component
 * @param {CiiRatingCardProps} props - The props for the CiiRatingCard component.
 * @param {boolean} props.isLoading - Indicates if the card is in a loading state. Default is `false`.
 * @param {object} props.ratingsPerDistance - Contains data for the year to date and forecasted Cii ratings and distances.
 *
 * @returns {ReactElement} The CiiRatingCard component.
 */
export const CiiRatingCard = ({ isLoading = false, ratingsPerDistance }: CiiRatingCardProps) => {
  // TODO: Change the fallback logic
  const { yearToDateCiiRating, forecastedCiiRating, yearToDateDistanceSailed, forecastedDistanceSailed } =
    ratingsPerDistance ?? {};
  const isYearToDateCiiRatingLoading =
    !isLoading && (yearToDateCiiRating !== undefined || yearToDateDistanceSailed !== undefined);
  const isForecastedCiiRatingLoading =
    !isLoading && (forecastedCiiRating !== undefined || forecastedDistanceSailed !== undefined);
  const ytdRating = yearToDateCiiRating ?? Rating.E;
  const ytdDescription = `${parseToThousands(yearToDateDistanceSailed)} ${text.nm} ${text.actual}`;
  const eoyRating = forecastedCiiRating ?? Rating.E;
  const eoyDescription = `${parseToThousands(forecastedDistanceSailed)} ${text.nm} ${text.forecasted}`;

  return (
    <Card w='full' justify='center' gap={4} px={{ base: 4, md: 6 }} py={{ base: 4, md: 5 }}>
      <Text fontSize={{ base: 'md', md: 'xl' }} fontWeight='medium'>
        {text.cii_rating}
      </Text>
      <Flex direction={{ base: 'row', md: 'column' }} gap={{ base: 0, md: 4 }} justify='space-between'>
        <RatingInfo
          rating={ytdRating}
          title={text.current_ytd}
          description={ytdDescription}
          isLoading={!isYearToDateCiiRatingLoading}
        />
        <RatingInfo
          rating={eoyRating}
          title={text.forecasted_eoy}
          description={eoyDescription}
          isLoading={!isForecastedCiiRatingLoading}
        />
      </Flex>
    </Card>
  );
};
